import React, { useState, useEffect } from 'react';
import { Calendar, User, Phone, Upload } from 'lucide-react';

import { supabase } from '../../../lib/supabase';
import { useStudentOnboardingStore } from '../../../store/studentOnboarding.store';
import { Button } from '../../../components/ui/Button';
import { ImageCropper } from '../../../components/ImageCropper';

interface ProfileStepProps {
  onComplete: () => void;
}

/**
 * Format phone number for display
 * Supports US format (10 digits) and international format (starts with +)
 * 
 * US format: (503) 555-1234
 * International format: +1 503 555 1234 (preserves + prefix)
 */
function formatPhoneNumber(input: string): string {
  const digits = input.replace(/\D/g, '');
  const hasPlus = input.trim().startsWith('+');
  
  // If starts with +, treat as international
  if (hasPlus) {
    // International format: +1 503 555 1234
    // Keep + prefix and format with spaces
    if (digits.length === 0) return '+';
    if (digits.length <= 1) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 1)} ${digits.slice(1)}`;
    if (digits.length <= 7) return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}`;
    if (digits.length <= 11) {
      return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    // For numbers longer than 11 digits (extensions)
    return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
  }
  
  // US format: (503) 555-1234 (default for 10-digit numbers)
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  // Cap at 10 digits for US format
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  // If more than 10 digits without +, treat as international without + prefix
  // Format as: 1 503 555 1234 (user can add + manually)
  if (digits.length <= 11) {
    return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)} ${digits.slice(11)}`;
}

export function ProfileStep({ onComplete }: ProfileStepProps) {
  const { profileData, setProfileData, setProfileComplete } = useStudentOnboardingStore();
  const [firstName, setFirstName] = useState(profileData.firstName);
  const [lastName, setLastName] = useState(profileData.lastName);
  const [phone, setPhone] = useState(profileData.phone);
  const [birthdate, setBirthdate] = useState(profileData.birthdate || '');
  const [avatarUrl, setAvatarUrl] = useState(profileData.avatarUrl);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: string } | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Load existing profile data if available
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('first_name, last_name, phone, dob, avatar_url')
          .eq('id', session.user.id)
          .single();

        if (profile) {
          setFirstName(profile.first_name || session.user.user_metadata?.first_name || '');
          setLastName(profile.last_name || session.user.user_metadata?.last_name || '');
          // Format phone number when loading from database
          const phoneToDisplay = profile.phone ? formatPhoneNumber(profile.phone) : '';
          setPhone(phoneToDisplay);
          setBirthdate(profile.dob || '');
          // Prefer avatar from store (uploaded in previous step), fallback to database
          const avatarToUse = profileData.avatarUrl || profile.avatar_url || null;
          setAvatarUrl(avatarToUse);
          // Sync store with database value if store is empty but database has one
          if (!profileData.avatarUrl && profile.avatar_url) {
            setProfileData({ avatarUrl: profile.avatar_url });
          }
        } else {
          // Use metadata from signup
          setFirstName(session.user.user_metadata?.first_name || '');
          setLastName(session.user.user_metadata?.last_name || '');
          // Format phone from store if available
          if (profileData.phone) {
            setPhone(formatPhoneNumber(profileData.phone));
          }
          // Use avatar from store if available
          if (profileData.avatarUrl) {
            setAvatarUrl(profileData.avatarUrl);
          }
        }
      }
    };
    loadUser();
  }, [profileData.avatarUrl, profileData.phone, setProfileData]);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setOriginalFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file: File) => {
    if (!user) {
      setError('Please wait for authentication to complete.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Ensure we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        setError('Please wait for authentication to complete. If this persists, please refresh the page.');
        setUploading(false);
        return;
      }

      // Verify user ID matches session
      if (user.id !== session.user.id) {
        setUser(session.user);
      }

      const fileExt = file.name.split('.').pop() || 'jpg';
      // RLS policy requires first folder to be auth.uid() - don't include 'avatars/' prefix
      // When using .from('avatars'), Supabase handles the bucket name
      const filePath = `${session.user.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(data.publicUrl);
      setProfileData({ avatarUrl: data.publicUrl });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileSelect(file);
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowCropper(false);
    
    // Convert blob URL to File
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();
    const file = new File([blob], originalFile?.name || 'avatar.png', {
      type: blob.type || 'image/png',
    });

    // Upload the cropped image
    await uploadFile(file);
    
    // Clean up
    URL.revokeObjectURL(croppedImageUrl);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!user) {
      setError('User not found. Please sign in again.');
      return;
    }

    setSaving(true);

    try {
      // Strip phone formatting before saving (store digits only, preserve + for international)
      const phoneDigits = phone.trim();
      const cleanedPhone = phoneDigits ? phoneDigits.replace(/[^\d+]/g, '') : null;

      // Update profile data in store
      setProfileData({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(), // Keep formatted version in store for display
        birthdate: birthdate || null,
        avatarUrl,
      });

      // Update user profile in database
      const updateData: {
        first_name: string;
        last_name: string;
        phone: string | null;
        dob: string | null;
        avatar_url: string | null;
        profile_complete: boolean;
        updated_at: string;
      } = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: cleanedPhone || null, // Store cleaned version in database
        dob: birthdate || null,
        avatar_url: avatarUrl || null,
        profile_complete: true,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update auth metadata
      await supabase.auth.updateUser({
        data: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
      });

      setProfileComplete(true);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Complete Your Profile</h2>
        <p className="text-[#A9A3A3] mt-2">Tell us a bit about yourself</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Avatar Display (from previous step) - Single display with edit option */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-[#504A4A] shadow-lg"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-[#363232] flex items-center justify-center border-4 border-[#504A4A]">
                <User size={48} className="text-[#A9A3A3]" />
              </div>
            )}
            <label
              htmlFor="avatar-upload"
              className="absolute bottom-0 right-0 w-10 h-10 bg-[#9B1421] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#7A0F1A] transition-colors shadow-lg border-2 border-[#221F1F]"
              title="Change photo"
            >
              <Upload size={18} className="text-white" />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
          {uploading && (
            <p className="text-sm text-[#A9A3A3] mt-2">Uploading...</p>
          )}
          {avatarUrl && !uploading && (
            <p className="text-xs text-[#A9A3A3] mt-1">Click icon to change photo</p>
          )}
        </div>

        {/* First Name */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            First Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            maxLength={50}
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            placeholder="John"
          />
        </div>

        {/* Last Name */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Last Name <span className="text-rollops-accent">*</span>
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            maxLength={50}
            className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            placeholder="Doe"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Phone Number
          </label>
          <div className="relative">
            <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]" />
            <input
              type="tel"
              value={phone}
              onChange={handlePhoneChange}
              maxLength={20}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              placeholder="(555) 123-4567 or +1 555 123 4567"
            />
          </div>
        </div>

        {/* Birthdate */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Date of Birth
          </label>
          <div className="relative">
            <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]" />
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={saving || uploading}
          variant="red"
          size="lg"
          className="w-full font-semibold"
        >
          {saving ? 'Saving...' : 'Continue'}
        </Button>
      </form>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          key={imageToCrop} // Force remount when image changes to reset cropper state
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          cropShape="round"
          minZoom={1}
          maxZoom={3}
        />
      )}
    </div>
  );
}
